import { Link } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useBranchLabels } from '../widget-hooks/useBranchLabels';
import { fetchPublicBranches } from '../widget-api/public-branches';
import type { PublicBranchItem } from '../widget-api/public-branches';
import type { PublicOrganizationBranchListItem } from '../widget-api/public-widget';
import { useAsync } from '../widget-hooks/useAsync';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';
import { BranchDetailCard } from '../widget-ui/branch-detail-card';

type BranchDetailRow = {
  id: number;
  name: string;
  address: string | null;
  working_hours: string | null;
  phone: string | null;
  comment?: string | null;
};

function fromPublicBranch(b: PublicBranchItem): BranchDetailRow {
  return {
    id: b.id,
    name: b.name,
    address: b.address,
    working_hours: b.working_hours,
    phone: b.phone,
  };
}

function fromShowcaseBranch(b: PublicOrganizationBranchListItem): BranchDetailRow {
  return {
    id: b.id,
    name: b.name,
    address: b.address,
    working_hours: b.working_hours,
    phone: b.phone,
  };
}

export default function BranchesPage() {
  useWidgetSeo(
    'Филиалы и адреса',
    'Список филиалов, адреса, схема проезда, телефоны и режим работы.'
  );
  const suffix = useQuerySuffix();
  const { orgSlug, widgetConfig, guestShowcaseOrg, bootstrapHint, organizationId } = useEmbedBootstrap();
  const branchLabels = useBranchLabels();

  const { data: allBranches, loading, error } = useAsync(() => fetchPublicBranches(), []);

  const forOrg = orgSlug
    ? (allBranches ?? []).filter((b) => b.org_slug === orgSlug)
    : [];

  const orgTimezone = widgetConfig?.timezone ?? guestShowcaseOrg?.timezone ?? 'Europe/Moscow';
  const showDefaultBranchClip =
    organizationId != null &&
    organizationId > 0 &&
    ((widgetConfig != null && widgetConfig.branches.length > 1) ||
      (guestShowcaseOrg != null && guestShowcaseOrg.branches.length > 1) ||
      (!loading && forOrg.length > 1));

  /** Несколько филиалов: список из подписанного виджета + публичный каталог (адрес/телефон). */
  const branchRowsForPicker: BranchDetailRow[] | null = (() => {
    if (widgetConfig && widgetConfig.branches.length > 1) {
      return widgetConfig.branches.map((b) => {
        const pub = forOrg.find((x) => x.id === b.id);
        return {
          id: b.id,
          name: b.name,
          address: pub?.address ?? null,
          working_hours: pub?.working_hours ?? null,
          phone: pub?.phone ?? null,
        };
      });
    }
    if (!loading && forOrg.length > 1) {
      return forOrg.map(fromPublicBranch);
    }
    return null;
  })();

  const singleBranchRowForWidget: BranchDetailRow | null = (() => {
    if (!widgetConfig || widgetConfig.branches.length !== 1) return null;
    const [wb] = widgetConfig.branches;
    if (!wb) return null;
    const pub = forOrg.find((x) => x.id === wb.id);
    return {
      id: wb.id,
      name: wb.name,
      address: pub?.address ?? null,
      working_hours: pub?.working_hours ?? null,
      phone: pub?.phone ?? null,
      comment: null,
    };
  })();

  if (bootstrapHint) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (guestShowcaseOrg && !widgetConfig && guestShowcaseOrg.branches.length <= 1) {
    const [b] = guestShowcaseOrg.branches;
    if (!b) {
      return (
        <div className="space-y-4 w-full min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
          <p className="text-sm text-[var(--text-muted)]">Для этой организации нет активных точек в каталоге.</p>
        </div>
      );
    }
    const row: BranchDetailRow = {
      ...fromShowcaseBranch(b),
      comment: guestShowcaseOrg.description?.trim() || null,
    };
    return (
      <div className="space-y-4 w-full min-w-0">
        <header className="space-y-1">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.singular}</span></h1>
          <p className="text-sm text-[var(--text-muted)]">
            Одна точка в каталоге — расписание и услуги будут для этой организации.
          </p>
        </header>
        <ul className="flex flex-col gap-2.5">
          <BranchDetailCard
            branchId={row.id}
            name={row.name}
            address={row.address}
            workingHours={row.working_hours}
            phone={row.phone}
            comment={row.comment}
            organizationId={organizationId}
            timezone={orgTimezone}
            showDefaultBranchToggle={showDefaultBranchClip}
            pickBranch
          />
        </ul>
        <p>
          <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.schedule}${suffix}`}>
            Перейти к расписанию
          </Link>
        </p>
      </div>
    );
  }

  if (guestShowcaseOrg && guestShowcaseOrg.branches.length > 1 && !widgetConfig) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <header className="space-y-1">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
          <p className="text-sm text-[var(--text-muted)]">
            Выберите {branchLabels.singularLc} — расписание и услуги будут для выбранной точки.
          </p>
        </header>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {guestShowcaseOrg.branches.map((b) => (
            <BranchDetailCard
              key={b.id}
              branchId={b.id}
              name={b.name}
              address={b.address}
              workingHours={b.working_hours}
              phone={b.phone}
              organizationId={organizationId}
              timezone={orgTimezone}
              showDefaultBranchToggle={showDefaultBranchClip}
              pickBranch
            />
          ))}
        </ul>
      </div>
    );
  }

  if (branchRowsForPicker && branchRowsForPicker.length > 1) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <header className="space-y-1">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Выбор <span className="capitalize">{branchLabels.genitiveSingularLc}</span></h1>
          <p className="text-sm text-[var(--text-muted)]">
            У организации несколько {branchLabels.genitivePluralLc}: выберите точку для расписания и услуг.
          </p>
        </header>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {branchRowsForPicker.map((b) => (
            <BranchDetailCard
              key={b.id}
              branchId={b.id}
              name={b.name}
              address={b.address}
              workingHours={b.working_hours}
              phone={b.phone}
              organizationId={organizationId}
              timezone={orgTimezone}
              showDefaultBranchToggle={showDefaultBranchClip}
              pickBranch
            />
          ))}
        </ul>
      </div>
    );
  }

  if (!orgSlug) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
        <p className="text-sm text-[var(--text-muted)]">
          Укажите org_slug в ссылке виджета, чтобы увидеть {branchLabels.pluralLc} организации.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="h-7 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="flex flex-col gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-24 w-full animate-pulse rounded-xl bg-[var(--bg-soft)]" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      </div>
    );
  }

  if (forOrg.length === 1) {
    const [b] = forOrg;
    if (!b) {
      return null;
    }
    const row = fromPublicBranch(b);

    return (
      <div className="space-y-4 w-full min-w-0">
        <header className="space-y-1">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
          <p className="text-sm text-[var(--text-muted)]">
            {widgetConfig && widgetConfig.branches.length === 1 && widgetConfig.is_network !== true
              ? ''
              : `В каталоге один ${branchLabels.singular} — можно перейти к расписанию с этой точкой.`}
          </p>
        </header>
        <ul className="flex flex-col gap-2.5">
          <BranchDetailCard
            branchId={row.id}
            name={row.name}
            address={row.address}
            workingHours={row.working_hours}
            phone={row.phone}
            organizationId={organizationId}
            timezone={orgTimezone}
            showDefaultBranchToggle={showDefaultBranchClip}
            pickBranch
          />
        </ul>
        <p>
          <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.schedule}${suffix}`}>
            Перейти к расписанию
          </Link>
        </p>
      </div>
    );
  }

  if (widgetConfig && singleBranchRowForWidget && forOrg.length === 0 && !loading) {
    const row = singleBranchRowForWidget;
    const oneStudio =
      widgetConfig.branches.length === 1 && widgetConfig.is_network !== true;
    return (
      <div className="space-y-4 w-full min-w-0">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">{oneStudio ? branchLabels.singular : branchLabels.plural}</h1>
          <p className="text-sm text-[var(--text-muted)]">
            {oneStudio
              ? 'Одна точка в приложении организации — данные ниже.'
              : 'Выберите точку ниже или откройте расписание.'}
          </p>
        </header>
        <ul className="flex flex-col gap-2.5">
          <BranchDetailCard
            branchId={row.id}
            name={row.name}
            address={row.address}
            workingHours={row.working_hours}
            phone={row.phone}
            organizationId={organizationId}
            timezone={orgTimezone}
            showDefaultBranchToggle={showDefaultBranchClip}
            pickBranch
          />
        </ul>
        <p>
          <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.schedule}${suffix}`}>
            Перейти к расписанию
          </Link>
        </p>
      </div>
    );
  }

  if (!forOrg.length) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-bold tracking-tight text-[var(--text)]"><span className="capitalize">{branchLabels.plural}</span></h1>
        <p className="text-sm text-[var(--text-muted)]">
          Для этой организации нет отдельных {branchLabels.genitivePluralLc} в публичном списке.
        </p>
      </div>
    );
  }

  return null;
}
